import { ReactNode, useCallback, useMemo } from 'react';

import {
  useReadBackofficeResourceGroupsToBuildTreeQuery,
  useReadSuperUserRolesForOwnUserQuery,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { PermissionsContext } from '@/shared/features/permissions/context/permissions-context';
import { selectPermissionResources } from '@/shared/features/permissions/store/permissions.slice';
import { buildPermissionModel } from '@/shared/features/permissions/utils/build-permission-model';
import { TPermissionResources } from '@/shared/features/permissions/utils/build-permission-model.types';
import { selectUser } from '@/shared/redux/api/auth/auth-api';
import { useAppSelector } from '@/shared/redux/hooks/hooks';

export const BackofficePermissionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const user = useAppSelector(selectUser);
  // Mounted above ProtectedRoute; don't fetch until the user is authenticated
  // AND past MFA.
  const skip = !user || !user.mfaEnabled;

  const {
    data: policiesData,
    error: policiesError,
    isLoading: isPoliciesLoading,
    refetch: refetchPolicies,
  } = useReadSuperUserRolesForOwnUserQuery({}, { skip });
  const {
    data: groupsData,
    error: groupsError,
    isLoading: isGroupsLoading,
    refetch: refetchGroups,
  } = useReadBackofficeResourceGroupsToBuildTreeQuery({}, { skip });
  const resources = useAppSelector(selectPermissionResources);

  const isLoading = isPoliciesLoading || isGroupsLoading;
  const error =
    policiesData === undefined && policiesError
      ? policiesError
      : groupsData === undefined && groupsError
        ? groupsError
        : undefined;
  const retry = useCallback(() => {
    void refetchPolicies();
    void refetchGroups();
  }, [refetchGroups, refetchPolicies]);

  const model = useMemo(() => {
    const policies =
      policiesData?.readSuperUserRolesForOwnUser?.items?.flatMap(
        ({ policies }) => policies,
      ) ?? [];

    const resourceItems: TPermissionResources = Object.fromEntries(
      Object.entries(resources).map(([entity, byId]) => [
        entity,
        Object.entries(byId).map(([id, entry]) => ({
          id,
          groupId: entry.groupId,
          tags: entry.tags,
        })),
      ]),
    );

    return buildPermissionModel({
      policies,
      resources: resourceItems,
      groups: groupsData?.readAllResourceGroups?.items ?? [],
      readableIdsAuthoritative: false,
    });
  }, [policiesData, groupsData, resources]);

  return (
    <PermissionsContext.Provider value={{ model, isLoading, error, retry }}>
      {children}
    </PermissionsContext.Provider>
  );
};
